// The panel every manifest-declared X-ray gets, until it authors its own.
//
// Before this existed, a manifest lens got a tab and a substrate and then fell
// through the panel dispatch to the default body — so selecting "Gate" showed the
// Terms chord diagram. A tab that renders the wrong panel is worse than a missing
// tab: the status line says the right thing, so it reads as the lens being broken
// rather than absent.
//
// Generic on purpose. It renders only what the manifest contract guarantees —
// groups with a label, ops, spread, a time span, plus whatever the lens folded in —
// and asks the manifest the two questions only the lens can answer: what to say
// when the window is empty (`emptyNote`), and why THIS op has no payload
// (`explainMissing`). A lens that needs more than this authors its own panel; this
// is the floor, not the ceiling.
//
// EVERY PANEL OWNS ITS SCROLLING. The host wrapper is `overflow-clip` — a true
// non-scrollable crop — deliberately, so that no wrapper becomes a competing
// scroll region while sticky headers pin to each panel's own scroll root. A panel
// returning a plain div therefore renders its overflow into a crop and cannot be
// scrolled at all, which is exactly how this one shipped: content correct, bottom
// unreachable.
import { useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";

const EYEBROW = "text-[9px] uppercase tracking-[0.08em] leading-[1.2] text-[color:var(--eyebrow)]";
const NOTE = "text-[10px] leading-[1.4] text-[color:var(--muted)]";

function when(ts) {
  if (ts == null) return "";
  const d = ts instanceof Date ? ts : new Date(typeof ts === "number" ? ts : String(ts));
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const tail = (p, segs = 2) => {
  const parts = String(p || "").split(/[/\\]/).filter(Boolean);
  return parts.length <= segs ? String(p || "") : `…/${parts.slice(-segs).join("/")}`;
};

// A rolled-up count map read as "biggest first", which is what a reader scans.
function countLine(counts) {
  const entries = Object.entries(counts || {}).filter(([, n]) => n);
  if (!entries.length) return null;
  return entries.sort((a, b) => b[1] - a[1]).map(([k, n]) => `${n} ${k}`).join(" · ");
}

export default function ManifestXrayPanel({ lens, scan, built, labelOf, traceOf, onSelectTrace, windowNote = null }) {
  const [open, setOpen] = useState(() => new Set());
  const groups = built?.groups || [];
  const scanning = scan?.status === "scanning";

  const actors = useMemo(() => (built?.actors || []).slice(0, 6), [built]);

  if (scanning) {
    // aria-busy is the single detectable "not ready" marker; a visual-only cue is
    // invisible to anything reading the DOM.
    return (
      // `content-start` is load-bearing: `h-full` on a grid with implicit rows
      // stretches the tracks to fill the panel, so three 14px placeholders spaced
      // by a quarter of the viewport each read as banding across an empty page
      // rather than as text loading.
      <div aria-busy="true" className="grid min-h-0 h-full min-w-0 content-start grid-cols-[minmax(0,1fr)] gap-[4px] px-[12px] py-[8px]">
        <span className={NOTE}>Hydrating raw payloads {scan.done}/{scan.total}…</span>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--control-bg)]" />
        ))}
      </div>
    );
  }

  if (!built || !groups.length) {
    // The manifest owns the first sentence: only the lens knows what its absence
    // means. The host owns the second: only it knows how much window the zero
    // was measured over.
    return (
      <div className="grid min-h-0 h-full min-w-0 content-start gap-[4px] px-[12px] py-[8px]">
        <span className={NOTE}>{lens.emptyNote}</span>
        {windowNote ? <span className={NOTE}>{windowNote}</span> : null}
      </div>
    );
  }

  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid min-w-0 grid-cols-[minmax(0,1fr)]">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[2px] px-[12px] py-[5px]">
        <span className={NOTE}>
          {groups.length} group{groups.length > 1 ? "s" : ""} · {built.totals?.ops ?? 0} run
          {(built.totals?.ops ?? 0) > 1 ? "s" : ""}
        </span>
        {actors.map((a) => (
          <span key={a.id || a.name} className={NOTE}>
            {labelOf ? labelOf({ id: a.id, label: a.name }) : a.name} {a.ops}
          </span>
        ))}
      </div>

      {groups.map((g) => {
        const isOpen = open.has(g.key);
        // Whatever the lens folded in, surfaced without the panel needing to know
        // its vocabulary. `stages`/`outcomes`/`kinds` are the shapes seen so far.
        const rolled = countLine(g.stages) || countLine(g.outcomes) || countLine(g.kinds);
        return (
          <div key={g.key} className="min-w-0 border-t border-[color:var(--line-soft)]">
            <button
              type="button"
              onClick={() => setOpen((cur) => {
                const next = new Set(cur);
                if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
                return next;
              })}
              className="flex w-full min-w-0 cursor-pointer items-baseline gap-[8px] px-[12px] py-[4px] text-left"
            >
              <span className="min-w-0 flex-1 truncate text-[11.5px] leading-[1.4] text-[color:var(--ink)]">
                {g.label}
              </span>
              {g.kind ? <span className={`${NOTE} shrink-0`}>{g.kind}</span> : null}
              <span className={`${NOTE} shrink-0`}>
                {g.ops.length} run{g.ops.length > 1 ? "s" : ""}
              </span>
            </button>

            <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[2px] px-[12px] pb-[5px]">
              {rolled ? <span className={NOTE}>{rolled}</span> : null}
              {/* The caller's own section names, when the lens collected them. */}
              {(g.labels || []).slice(0, 8).map((l) => (
                <span key={l} className="rounded-[2px] border border-[color:var(--line)] px-[5px] text-[10px] leading-[1.5] text-[color:var(--ink)]">
                  {l}
                </span>
              ))}
              {(g.filters || []).length ? <span className={NOTE}>kept: {g.filters.slice(0, 5).join(" · ")}</span> : null}
              {(g.excludes || []).length ? <span className={NOTE}>dropped: {g.excludes.slice(0, 5).join(" · ")}</span> : null}
            </div>

            {isOpen ? (
              <div className="min-w-0 border-t border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[12px] py-[6px]">
                {g.ops.map((op, i) => {
                  // Only the lens can say why an op has no payload — four states, of
                  // which one is the tool's own output. Presenting the others as
                  // observed evidence is what made an earlier panel read as
                  // inconsistent.
                  const missing = lens.explainMissing(op);
                  return (
                    <div key={op.key || i} className="min-w-0 pb-[8px]">
                      <div className="flex min-w-0 items-baseline gap-[8px] pb-[2px]">
                        <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                          {when(traceOf?.get(op.promptId)?.timestamp ?? op.ts)}
                        </span>
                        <span className={`${NOTE} min-w-0 truncate`}>
                          {[op.engine, op.endpointName && labelOf
                            ? labelOf({ id: op.endpointId, label: op.endpointName })
                            : op.endpointName].filter(Boolean).join(" · ")}
                        </span>
                        {op.promptId && onSelectTrace ? (
                          <button
                            type="button"
                            onClick={() => onSelectTrace(op.promptId)}
                            className="ml-auto shrink-0 cursor-pointer text-[10px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                          >
                            open trace →
                          </button>
                        ) : null}
                      </div>

                      {(op.steps || []).length ? (
                        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] rounded-[2px] bg-[color:var(--control-bg)] py-[2px]">
                          {op.steps.filter((s) => s.kind !== "control").slice(0, 24).map((s, j) => (
                            <div key={j} className="flex min-w-0 items-baseline gap-[8px] px-[8px] font-mono text-[10.5px] leading-[1.55]">
                              <span className={`${EYEBROW} w-[70px] shrink-0`}>{s.kind}</span>
                              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[color:var(--ink)]">{s.cmd}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {/* Declared fields, for a lens whose payload is named values
                          rather than steps or a command stream. Same label/value
                          grid as `steps` — a runtime lens has no other way to show
                          what it extracted, and without this it renders as a
                          timestamp and an endpoint. */}
                      {(op.fields || []).length ? (
                        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] rounded-[2px] bg-[color:var(--control-bg)] py-[2px]">
                          {op.fields.slice(0, 12).map((f, j) => (
                            <div key={j} className="flex min-w-0 items-baseline gap-[8px] px-[8px] font-mono text-[10.5px] leading-[1.55]">
                              <span className={`${EYEBROW} w-[110px] shrink-0 truncate`}>{f.name}</span>
                              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[color:var(--ink)]">{f.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {(op.checks || []).length ? (
                        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] rounded-[2px] bg-[color:var(--control-bg)] py-[2px]">
                          {op.checks.map((c, j) => (
                            <div key={j} className="flex min-w-0 items-baseline gap-[8px] px-[8px] text-[10.5px] leading-[1.55]">
                              <span className={`${EYEBROW} w-[70px] shrink-0`}>{c.kind}</span>
                              <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">
                                {c.tool}{c.scope?.length ? ` · ${c.scope.slice(0, 4).join(", ")}` : ""}
                              </span>
                              <span className={`${NOTE} shrink-0`}>{c.outcome}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {/* Output WRAPS. The panel's own ScrollArea is the only
                          vertical scroll surface, so a nested scroller would strand
                          content a reader cannot find. */}
                      {op.output ? (
                        <pre className="mt-[3px] min-w-0 whitespace-pre-wrap break-words rounded-[2px] border border-[color:var(--line-soft)] px-[8px] py-[4px] font-mono text-[10.5px] leading-[1.5] text-[color:var(--ink)]">
                          {op.output.split("\n").slice(0, 18).join("\n")}
                        </pre>
                      ) : null}

                      {missing ? <span className={`${NOTE} block pt-[3px]`}>{missing}</span> : null}
                      {op.redirectedTo && !op.output ? (
                        <span className={`${NOTE} block`}>{tail(op.redirectedTo)}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </ScrollArea>
  );
}
